import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect } from 'react';

import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import {
	ChunkLoadRecoveryOutcome,
	claimReloadForError,
	reloadPage as defaultReloadPage,
	registerBoundaryChunkLoadDisplay,
} from './chunk-load-recovery.js';

export interface ChunkLoadErrorDisplayProps {
	/** Whether the page reloads itself now or asks the user to reload. */
	outcome: ChunkLoadRecoveryOutcome;
	/** Reloads the page. Defaults to a full browser reload. */
	reloadPage?: () => void;
	/** The error shown; another display of the same error won't reload the page again. */
	error?: unknown;
	/** Whether an error boundary renders it, which keeps the page-level stale-deploy prompt closed meanwhile. */
	inBoundary?: boolean;
}

/**
 * The error surface for a chunk that failed to load. With a `Reloaded` outcome it reloads the page on mount and
 * shows a loading state until the browser navigates; with `Fallback` it explains the failure and offers a
 * manual reload.
 *
 * @param props - The recovery outcome, and optionally the reload to run.
 * @returns The loading state or the reload prompt.
 */
export function ChunkLoadErrorDisplay({
	outcome,
	reloadPage = defaultReloadPage,
	error,
	inBoundary = false,
}: ChunkLoadErrorDisplayProps) {
	const el = useDataElTagGenerator(ChunkLoadErrorDisplay);
	const isAutoReloading = outcome === ChunkLoadRecoveryOutcome.Reloaded;

	useEffect(() => {
		if (isAutoReloading && claimReloadForError(error)) {
			reloadPage();
		}
	}, [isAutoReloading, reloadPage, error]);

	useEffect(() => (inBoundary ? registerBoundaryChunkLoadDisplay() : undefined), [inBoundary]);

	if (isAutoReloading) {
		return (
			<div {...el('reloading')} className='grid min-h-0 min-w-0 grow place-items-center p-4'>
				<div className='flex flex-col items-center gap-3'>
					<CircularProgress />
					<p className='text-gray-a11'>Loading the latest version…</p>
				</div>
			</div>
		);
	}

	return (
		<div {...el()} className='grid min-h-0 min-w-0 grow place-items-center p-4'>
			<div {...el('container')} className='flex flex-col gap-6'>
				<div className='flex min-w-0 grow basis-auto flex-col gap-3'>
					<h1 className='text-3xl font-bold'>This page needs a reload</h1>
					<p className='text-gray-a11'>
						Part of Cadstrom didn&apos;t load, usually because a new version was just released.
					</p>
				</div>

				<Button onClick={reloadPage}>
					<span>Reload page</span>
				</Button>
			</div>
		</div>
	);
}
